import { type ReactElement } from 'react';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@citibox-panel/ui';
import { formatSince } from '../lib/formatSince';
import type { ExcludedUser } from '../lib/excludedUser';

export interface ExcludedUsersTableProps {
  users: ExcludedUser[];
  timeZone: string | null;
  // The row whose removal is in flight, so its button shows it while every other one waits.
  removingUserId: number | null;
  onRemove: (user: ExcludedUser) => void;
}

// TODO: i18n — copy en ES hasta Fase 1.
const HEADERS = ['NOMBRE', 'TELÉFONO', 'DESDE', 'ACCIONES'] as const;

// Fixed column sizing so a long name truncates inside its cell instead of pushing the button off
// the card; the actions column keeps a visible header because the façade has no visually-hidden
// text and an empty `th` leaves the column nameless.
export function ExcludedUsersTable({
  users,
  timeZone,
  removingUserId,
  onRemove,
}: ExcludedUsersTableProps): ReactElement {
  return (
    <Table
      size="small"
      layout="fixed"
      columnWidths={['38%', '24%', '18%', '20%']}
      aria-label="Usuarios excluidos"
    >
      <TableHead>
        <TableRow>
          {HEADERS.map((header) => (
            <TableCell key={header} component="th" scope="col">
              {/* Body tone, never `secondary`: that one measures ≈2.4:1 on this surface. */}
              <Text variant="overline" component="span" bold dense>
                {header}
              </Text>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.userId}>
            <TableCell>
              {/* The cut is visual only: the accessible name stays whole and the hover gives back
                  what the eye lost. */}
              <Text variant="body2" noWrap title={user.name}>
                {user.name}
              </Text>
            </TableCell>
            {/* Verbatim E.164, as the ficha paints it: same string for the same person everywhere. */}
            <TableCell singleLine>
              <Text variant="body2">{user.phone}</Text>
            </TableCell>
            <TableCell singleLine>
              <Text variant="body2">{formatSince(user.since, timeZone)}</Text>
            </TableCell>
            <TableCell align="right">
              <Button
                variant="text"
                aria-label={`Quitar a ${user.name}`}
                disabled={removingUserId !== null}
                loading={removingUserId === user.userId}
                onClick={() => {
                  onRemove(user);
                }}
              >
                QUITAR
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
